export interface ApiError extends Error {
  status: number;
  url: string;
  body?: string;
  data?: Record<string, unknown>;
}

// Covers the complete read, including the response body. Write/submission helpers
// deliberately do not use this deadline.
export const DEFAULT_READ_TIMEOUT_MS = 30_000;

export class ReadTimeoutError extends Error {
  constructor() {
    super('The data request timed out. Please try again.');
    this.name = 'ReadTimeoutError';
  }
}

export interface ReadOptions {
  signal?: AbortSignal;
  timeoutMs?: number;
}

export async function get<TResult>(url: string, params?: Record<string, unknown>, options: ReadOptions = {}): Promise<TResult> {
  const timeoutMs = options.timeoutMs ?? DEFAULT_READ_TIMEOUT_MS;
  if (!Number.isFinite(timeoutMs) || timeoutMs <= 0) throw new Error('Read timeout must be a positive finite duration');
  const controller = new AbortController();
  const abort = () => controller.abort(options.signal?.reason);
  if (options.signal?.aborted) {
    abort();
    throw controller.signal.reason;
  }
  options.signal?.addEventListener('abort', abort, { once: true });
  let rejectAbort!: () => void;
  const aborted = new Promise<never>((_, reject) => {
    rejectAbort = () => reject(controller.signal.reason);
    controller.signal.addEventListener('abort', rejectAbort, { once: true });
  });
  const timer = setTimeout(() => controller.abort(new ReadTimeoutError()), timeoutMs);
  try {
    return await Promise.race([read<TResult>(url, params, controller.signal), aborted]);
  } finally {
    clearTimeout(timer);
    options.signal?.removeEventListener('abort', abort);
    controller.signal.removeEventListener('abort', rejectAbort);
  }
}

async function read<TResult>(url: string, params: Record<string, unknown> | undefined, signal: AbortSignal): Promise<TResult> {
  const queryString = buildQueryParams(params);

  const res = await fetch(queryString ? `${url}?${queryString}` : url, {
    method: 'GET',
    signal,
    headers: {
      Accept: 'application/json',
    },
  });

  const contentType = res.headers.get('content-type');
  const isJsonResponse = contentType?.includes('application/json');

  // Esplora and proxies may return plaintext errors. Preserve the status and
  // response body so callers can distinguish actionable backend failures.
  if (!res.ok) {
    let result: Record<string, unknown> | string = '';
    let errorMessage = `GET request failed: ${res.status} ${res.statusText}`;

    try {
      if (isJsonResponse) {
        result = (await res.json()) as Record<string, unknown>;
        errorMessage = (result.error as string) || (result.message as string) || errorMessage;
      } else {
        result = (await res.text()).trim();
        errorMessage = result || errorMessage;
      }
    } catch {
      if (signal.aborted) throw signal.reason;
    }

    const error = new Error(errorMessage) as ApiError;
    error.status = res.status;
    error.url = url;

    if (typeof result === 'object') {
      error.data = result;
    } else {
      error.body = result;
    }

    throw error;
  }
  if (isJsonResponse) {
    return (await res.json()) as TResult;
  }

  return (await res.text()) as TResult;
}

function buildQueryParams(params: Record<string, unknown> | undefined): string {
  if (!params) {
    return '';
  }

  const searchParams = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (Array.isArray(value)) {
      value.forEach((item) => {
        searchParams.append(key, item.toString());
      });
    } else if (value !== undefined && value !== null) {
      searchParams.append(key, value.toString());
    }
  }

  return searchParams.toString();
}
